export const getCameraIdFromVideoStreamUrl = (src) => {
  const match = src?.match(
    /\/v2\/integrations\/device\/camera\/([^/]+)\/video-stream\/index\.m3u8/,
  );
  if (!match) return null;

  // The id segment is URL-encoded in the stream URL. Decode it here so callers get the
  // raw id and re-encode exactly once when they build their own request URL (otherwise
  // ids with special characters get double-encoded and hit the wrong path).
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};

export const getTimeRangeBounds = (timeRanges) => {
  if (!timeRanges?.length) return null;

  const earliestAvailableTimestamp = timeRanges.start(0); // Gets the timestamp from the first available time range array
  const latestAvailableTimestamp = timeRanges.end(timeRanges.length - 1); // Gets the timestamp from the last available time range array

  if (!Number.isFinite(earliestAvailableTimestamp) || !Number.isFinite(latestAvailableTimestamp)) {
    return null;
  }

  if (latestAvailableTimestamp <= earliestAvailableTimestamp) return null;

  return {
    earliestAvailableTimestamp,
    latestAvailableTimestamp,
    availableVideoDuration: latestAvailableTimestamp - earliestAvailableTimestamp,
  };
};

// Returns the available video times (earliest and latest timestamps) and the duration of the available video data, or null if can't be determined
export const getAvailableVideoTimeRange = (video) => getTimeRangeBounds(video?.buffered);

// Returns the seekable video times (earliest and latest timestamps) and the duration of the seekable video data
export const getSeekableVideoTimeRange = (video) =>
  getTimeRangeBounds(video?.seekable) || getAvailableVideoTimeRange(video);

// Returns the number of seconds of video that have been loaded ahead of the current playback position
export const getVideoSecondsAheadOfCurrentTime = (video) => {
  const availableVideoTimes = getAvailableVideoTimeRange(video);
  if (!video || !availableVideoTimes || !Number.isFinite(video.currentTime)) return 0;

  return Math.max(0, availableVideoTimes.latestAvailableTimestamp - video.currentTime);
};

// Returns true if the video has enough video data to start playback, based on the required number of seconds ahead of the current time
export const hasEnoughVideoToStart = (video, requiredSeconds) => {
  return (
    video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA &&
    getVideoSecondsAheadOfCurrentTime(video) >= requiredSeconds
  );
};

// Skips to the newest available video timestamp, minus a cushion in seconds, if possible. Returns true if the skip was successful, false otherwise.
export const skipToNewestReadyVideoWithCushion = (video, cushionSeconds) => {
  const availableVideoTimes = getAvailableVideoTimeRange(video);
  if (!video || !availableVideoTimes) return false;

  const targetTime = Math.max(
    availableVideoTimes.earliestAvailableTimestamp,
    availableVideoTimes.latestAvailableTimestamp - cushionSeconds,
  );

  if (!Number.isFinite(targetTime)) return false;

  video.currentTime = targetTime;
  return true;
};

/**
 * Gets the current fullscreen element, if any. Returns null if no element is currently in fullscreen mode.
 * Handles cross-browser differences in the fullscreen API (some browsers use different prefixes for the fullscreen API).
 */
export const getFullscreenElement = (fullscreenDocument = document) =>
  fullscreenDocument.fullscreenElement ||
  fullscreenDocument.webkitFullscreenElement ||
  fullscreenDocument.msFullscreenElement;
